import React, { useState, useContext, useEffect } from 'react';
import { Modal, Form, Input, Switch, Select, App } from 'antd';
import { AuthContext } from '../../context/AuthContext';
import passwordResetService from '../../services/passwordResetService';
import logger from '../../utils/logger';

const { Option } = Select;

const AccountSettingsModal = ({ open, onClose }) => {
  const { currentUser } = useContext(AuthContext);
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Populate form when modal opens
  useEffect(() => {
    if (open && currentUser) {
      form.setFieldsValue({
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        email: currentUser.email,
        email_notifications: currentUser.email_notifications !== false, // default true
        timezone: currentUser.timezone || 'America/New_York',
      });
    }
  }, [open, currentUser, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await passwordResetService.updateSettings(values);

      message.success('Account settings updated successfully');
      onClose();
    } catch (error) {
      logger.error('Failed to update settings:', error);
      if (error.errorFields) {
        // Form validation error
        message.error('Please check the form for errors');
      } else {
        message.error(error.response?.data?.error || 'Failed to update settings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Account Settings"
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText="Save Changes"
      cancelText="Cancel"
      confirmLoading={loading}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="First Name"
          name="first_name"
          rules={[{ required: true, message: 'Please enter your first name' }]}
        >
          <Input placeholder="Enter first name" />
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="last_name"
          rules={[{ required: true, message: 'Please enter your last name' }]}
        >
          <Input placeholder="Enter last name" />
        </Form.Item>

        <Form.Item
          label="Email Address"
          name="email"
          tooltip="Email address cannot be changed from here"
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          label="Email Notifications"
          name="email_notifications"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="Timezone"
          name="timezone"
        >
          <Select placeholder="Select timezone">
            <Option value="America/New_York">Eastern Time (ET)</Option>
            <Option value="America/Chicago">Central Time (CT)</Option>
            <Option value="America/Denver">Mountain Time (MT)</Option>
            <Option value="America/Los_Angeles">Pacific Time (PT)</Option>
            <Option value="America/Anchorage">Alaska Time (AKT)</Option>
            <Option value="Pacific/Honolulu">Hawaii Time (HST)</Option>
            <Option value="Europe/London">London (GMT)</Option>
            <Option value="Europe/Paris">Paris (CET)</Option>
            <Option value="Asia/Tokyo">Tokyo (JST)</Option>
            <Option value="Asia/Shanghai">Shanghai (CST)</Option>
            <Option value="Australia/Sydney">Sydney (AEDT)</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AccountSettingsModal;
